// ─────────────────────────────────────────────────────────────────────────────
// LCCA NEXUS — Cost Breakdown Charts (Recharts)
// ─────────────────────────────────────────────────────────────────────────────
import {
  PieChart, Pie, Cell, Tooltip as RTooltip, Legend,
  LineChart, Line, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, ResponsiveContainer,
  ReferenceLine, Area, AreaChart,
} from 'recharts';
import { Card, CardHeader, CardBody } from '@/components/ui/index';
import { fmtShort, fmt, CHART_COLORS } from '@/lib/utils';
import type { LCCAResults, LCCAParameters } from '@lcca-nexus/shared';

interface ChartsProps {
  results: LCCAResults;
  params: LCCAParameters;
}

// ── Shared tooltip style ──────────────────────────────────────────────────────
const tooltipStyle = {
  background: '#0c1b2e',
  border: 'none',
  borderRadius: 8,
  color: '#fff',
  fontSize: 12,
  fontFamily: 'JetBrains Mono, monospace',
  boxShadow: '0 4px 24px rgba(0,0,0,0.3)',
};

const labelStyle = { color: '#9eb3cc', fontFamily: 'Nunito, sans-serif' };

export function CostBreakdownCharts({ results, params }: ChartsProps) {

  // ── Donut data ──────────────────────────────────────────────────────────────
  const donutData = [
    { name: 'Initial Cost',  value: results.initialCostPW,  color: CHART_COLORS.navy },
    { name: 'O&M',          value: results.omPW,             color: CHART_COLORS.amber },
    { name: 'Energy',       value: results.energyPW,         color: CHART_COLORS.teal },
    { name: 'Replacement',  value: results.replacementPW,    color: CHART_COLORS.red },
  ].filter(d => d.value > 0);

  // ── Cumulative area chart data ──────────────────────────────────────────────
  const cumulData = [
    { year: 0, value: params.initialCost, label: 'Y0' },
    ...results.cashFlows.map(y => ({ year: y.year, value: y.cumulativePW, label: `Y${y.year}` })),
  ];

  // ── Annual stacked bar (sampled for readability) ──────────────────────────
  const step = Math.max(1, Math.floor(results.cashFlows.length / 16));
  const barData = results.cashFlows
    .filter(y => y.year % step === 0 || y.replacementCost > 0)
    .map(y => ({
      year: `Y${y.year}`,
      'O&M': Math.round(y.omCost),
      Energy: Math.round(y.energyCost),
      Replacement: Math.round(y.replacementCost),
    }));

  // ── Present worth scatter ────────────────────────────────────────────────
  const pwData = results.cashFlows.map(y => ({
    year: `Y${y.year}`,
    pw: Math.round(y.presentWorth),
    repl: y.replacementCost > 0,
  }));

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

      {/* 1. Donut – Cost Category Breakdown */}
      <Card className="animate-fade-up delay-75">
        <CardHeader>
          <div>
            <div className="font-display font-bold text-navy-900 text-sm">Cost Category Breakdown</div>
            <div className="text-xs text-navy-400">Present-worth share by category</div>
          </div>
        </CardHeader>
        <CardBody>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie
                data={donutData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={95}
                paddingAngle={3}
                dataKey="value"
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                labelLine={false}
              >
                {donutData.map((entry, i) => (
                  <Cell key={i} fill={entry.color} />
                ))}
              </Pie>
              <RTooltip
                contentStyle={tooltipStyle}
                formatter={(v: number) => [fmt(v), '']}
              />
            </PieChart>
          </ResponsiveContainer>
          {/* Legend */}
          <div className="flex flex-wrap gap-3 justify-center mt-2">
            {donutData.map(d => (
              <div key={d.name} className="flex items-center gap-1.5 text-xs font-medium text-navy-600">
                <div className="w-2.5 h-2.5 rounded-full" style={{ background: d.color }} />
                {d.name}
              </div>
            ))}
          </div>
        </CardBody>
      </Card>

      {/* 2. Area – Cumulative LCC */}
      <Card className="animate-fade-up delay-150">
        <CardHeader>
          <div>
            <div className="font-display font-bold text-navy-900 text-sm">Cumulative Life-Cycle Cost</div>
            <div className="text-xs text-navy-400">Year-by-year NPV accumulation</div>
          </div>
        </CardHeader>
        <CardBody>
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={cumulData} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="cumulGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor={CHART_COLORS.navy} stopOpacity={0.18} />
                  <stop offset="95%" stopColor={CHART_COLORS.navy} stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2ddd7" />
              <XAxis
                dataKey="year"
                tick={{ fontSize: 10, fontFamily: 'JetBrains Mono', fill: '#7a8ca0' }}
                tickFormatter={v => v === 0 ? 'Y0' : `Y${v}`}
                interval={Math.floor(results.cashFlows.length / 8)}
              />
              <YAxis
                tick={{ fontSize: 10, fontFamily: 'JetBrains Mono', fill: '#7a8ca0' }}
                tickFormatter={v => fmtShort(v)}
                width={60}
              />
              <RTooltip
                contentStyle={tooltipStyle}
                labelStyle={labelStyle}
                formatter={(v: number) => [fmt(v), 'Cumulative PW']}
                labelFormatter={v => `Year ${v}`}
              />
              <Area
                type="monotone"
                dataKey="value"
                stroke={CHART_COLORS.navy}
                strokeWidth={2}
                fill="url(#cumulGrad)"
                dot={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </CardBody>
      </Card>

      {/* 3. Stacked Bar – Annual Cost Flows */}
      <Card className="animate-fade-up delay-225">
        <CardHeader>
          <div>
            <div className="font-display font-bold text-navy-900 text-sm">Annual Cost Flow</div>
            <div className="text-xs text-navy-400">Undiscounted stacked by category</div>
          </div>
        </CardHeader>
        <CardBody>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={barData} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2ddd7" />
              <XAxis dataKey="year" tick={{ fontSize: 9, fontFamily: 'JetBrains Mono', fill: '#7a8ca0' }} />
              <YAxis tick={{ fontSize: 9, fontFamily: 'JetBrains Mono', fill: '#7a8ca0' }} tickFormatter={v => fmtShort(v)} width={55} />
              <RTooltip contentStyle={tooltipStyle} labelStyle={labelStyle} formatter={(v: number) => [fmt(v), '']} />
              <Bar dataKey="O&M"         stackId="a" fill={CHART_COLORS.amber} radius={[0,0,0,0]} />
              <Bar dataKey="Energy"      stackId="a" fill={CHART_COLORS.teal} />
              <Bar dataKey="Replacement" stackId="a" fill={CHART_COLORS.red} radius={[3,3,0,0]} />
            </BarChart>
          </ResponsiveContainer>
          <div className="flex gap-4 justify-center mt-2">
            {[['O&M', CHART_COLORS.amber], ['Energy', CHART_COLORS.teal], ['Replacement', CHART_COLORS.red]].map(([k, c]) => (
              <div key={k} className="flex items-center gap-1.5 text-xs font-medium text-navy-600">
                <div className="w-2.5 h-2.5 rounded" style={{ background: c }} />
                {k}
              </div>
            ))}
          </div>
        </CardBody>
      </Card>

      {/* 4. Bar – Discounted PW by year */}
      <Card className="animate-fade-up delay-300">
        <CardHeader>
          <div>
            <div className="font-display font-bold text-navy-900 text-sm">Present Worth by Year</div>
            <div className="text-xs text-navy-400">
              Discounted annual flows — <span className="text-red-500 font-semibold">red = replacement years</span>
            </div>
          </div>
        </CardHeader>
        <CardBody>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={pwData} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2ddd7" />
              <XAxis dataKey="year" tick={{ fontSize: 9, fontFamily: 'JetBrains Mono', fill: '#7a8ca0' }} interval={Math.floor(pwData.length / 10)} />
              <YAxis tick={{ fontSize: 9, fontFamily: 'JetBrains Mono', fill: '#7a8ca0' }} tickFormatter={v => fmtShort(v)} width={55} />
              <RTooltip contentStyle={tooltipStyle} labelStyle={labelStyle} formatter={(v: number) => [fmt(v), 'Present Worth']} />
              <Bar dataKey="pw" radius={[2,2,0,0]}>
                {pwData.map((entry, i) => (
                  <Cell
                    key={i}
                    fill={entry.repl ? CHART_COLORS.red : CHART_COLORS.blue}
                    fillOpacity={entry.repl ? 0.85 : 0.7}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </CardBody>
      </Card>

    </div>
  );
}
